import type { EChartsOption } from 'echarts'
import { axisTextStyle, formatCompactNumber, formatShare, isFiniteNumber, tooltipBase, tooltipHtml } from './format'
import { labelColorOn, seriesColor, type ChartTheme } from './palette'

/** Часть целого: стадия жизненного цикла, кабинет, канал. Цвет закреплён за частью, а не за местом. */
export interface StackedShareSegment {
  key: string
  label: string
  /** Слот категориальной палитры (0..7); по умолчанию — по порядку частей. `null` — «Прочее» (серый). */
  slot?: number | null
  /** Явный цвет (если часть — постоянная сущность со своим цветом). */
  color?: string
}

export interface StackedShareRow {
  key: string
  label: string
  /** Значения частей по порядку `segments`; `null` — нет данных (не ноль). */
  values: Array<number | null>
  sublabel?: string | null
}

export interface StackedShareInput {
  rows: StackedShareRow[]
  segments: StackedShareSegment[]
  /** Полное значение части в подсказке (по умолчанию — короткое число). */
  valueFormat?: (value: number) => string
  /** Подписи долей внутри полосы, если часть занимает не меньше этой доли (по умолчанию 12 %). */
  labelMinShare?: number
  /** Ширина колонки подписей строк, px. */
  rowLabelWidth?: number
}

/** Цвет части: явный, иначе слот палитры (по умолчанию — порядковый номер части), `null` — «Прочее». */
export function segmentColor(theme: ChartTheme, segment: StackedShareSegment, index: number): string {
  if (segment.color) return segment.color
  if (segment.slot === null) return theme.other
  return seriesColor(theme, segment.slot ?? index)
}

/** Доли частей строки (0..1). Строка без единого числа — все доли `null`; сумма ≤ 0 — тоже `null`. */
export function rowShares(values: Array<number | null>): Array<number | null> {
  const positive = values.map((value) => (isFiniteNumber(value) && value > 0 ? value : 0))
  const total = positive.reduce((sum, value) => sum + value, 0)
  if (!values.some(isFiniteNumber) || total <= 0) return values.map(() => null)
  return values.map((value) => (isFiniteNumber(value) ? Math.max(0, value) / total : null))
}

export function stackedShareHeight(rows: number): number {
  return Math.max(96, rows * 32 + 24)
}

/**
 * Полосы 100 %: каждая строка — целое, части — сегменты в фиксированном порядке и закреплённых цветах.
 * Между сегментами — зазор 2 px цвета поверхности (обводка 1 px с каждой стороны), концы полосы скруглены
 * на 4 px. Подпись доли — внутри сегмента, только если влезает; остальное — в подсказке и табличном виде.
 * Подсказка на строку перечисляет все части (значение крупно, подпись мельче).
 */
export function buildStackedShareOption(input: StackedShareInput, theme: ChartTheme): EChartsOption {
  const valueFormat = input.valueFormat ?? ((value: number) => formatCompactNumber(value))
  const minShare = input.labelMinShare ?? 0.12
  const shares = input.rows.map((row) => rowShares(row.values))
  const colors = input.segments.map((segment, index) => segmentColor(theme, segment, index))

  // Первая и последняя непустые части строки — для скругления концов.
  const ends = shares.map((row) => {
    let first = -1
    let last = -1
    row.forEach((share, index) => {
      if (isFiniteNumber(share) && share > 0) {
        if (first < 0) first = index
        last = index
      }
    })
    return { first, last }
  })

  return {
    backgroundColor: 'transparent',
    animationDuration: 300,
    textStyle: { fontFamily: theme.fontFamily },
    aria: { enabled: true },
    grid: { top: 4, left: 8, right: 12, bottom: 4, outerBoundsMode: 'same', outerBoundsContain: 'axisLabel' } as any,
    tooltip: {
      ...tooltipBase(theme),
      trigger: 'axis',
      axisPointer: { type: 'shadow', shadowStyle: { color: theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' } },
      formatter: (raw: any) => {
        const params = Array.isArray(raw) ? raw : [raw]
        const rowIndex = params[0]?.dataIndex ?? -1
        const row = input.rows[rowIndex]
        if (!row) return ''
        return tooltipHtml(
          theme,
          row.sublabel ? `${row.label} · ${row.sublabel}` : row.label,
          input.segments.map((segment, index) => {
            const value = row.values[index]
            return {
              color: colors[index],
              label: segment.label,
              value: isFiniteNumber(value) ? valueFormat(value) : '—',
              note: isFiniteNumber(shares[rowIndex][index]) ? formatShare(shares[rowIndex][index]) : undefined,
            }
          }),
        )
      },
    },
    xAxis: { type: 'value', min: 0, max: 1, show: false },
    yAxis: {
      type: 'category',
      inverse: true,
      data: input.rows.map((_row, index) => `r${index}`),
      triggerEvent: true,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        ...axisTextStyle(theme),
        color: theme.textSecondary,
        fontSize: 12,
        width: input.rowLabelWidth ?? 180,
        overflow: 'truncate',
        interval: 0,
        formatter: (key: string) => input.rows[Number(String(key).slice(1))]?.label ?? '',
      },
    },
    series: input.segments.map((segment, segmentIndex) => ({
      name: segment.label,
      type: 'bar',
      stack: 'total',
      barMaxWidth: 20,
      barCategoryGap: '35%',
      cursor: 'pointer',
      emphasis: { focus: 'none', itemStyle: { opacity: 0.85 } },
      data: shares.map((row, rowIndex) => {
        const share = row[segmentIndex]
        const { first, last } = ends[rowIndex]
        const radius = [
          segmentIndex === first ? 4 : 0,
          segmentIndex === last ? 4 : 0,
          segmentIndex === last ? 4 : 0,
          segmentIndex === first ? 4 : 0,
        ]
        return {
          value: isFiniteNumber(share) ? share : '-',
          itemStyle: { color: colors[segmentIndex], borderColor: theme.surface, borderWidth: 1, borderRadius: radius },
          label: {
            show: isFiniteNumber(share) && share >= minShare,
            color: labelColorOn(colors[segmentIndex], theme),
          },
        }
      }),
      label: {
        position: 'inside',
        fontSize: 11,
        fontFamily: theme.fontFamily,
        formatter: (params: any) => (isFiniteNumber(params.value) ? formatShare(params.value, 0) : ''),
      },
    })),
  } as EChartsOption
}
